import {
  formatHourValue,
  formatHours,
  type AdSyncConfigDto,
  type AdSyncResultDto,
  type AdminUserDto,
  type AdminUserMembershipDto,
  type UpdateAdSyncConfigRequest,
} from '@time-tracking/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useAdSyncConfig,
  useAdSyncNow,
  useAdminUserLines,
  useAdminUserMemberships,
  useAdminUsers,
  useSetUserActive,
  useUpdateAdSyncConfig,
  useUpdateUserHourTarget,
  useUpdateUserRole,
  useUpdateUserSystemStartDate,
} from '../../../api/admin.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';
// `chicagoTodayIso` caps the start-date picker at today: the API validates "not
// in the future" against the America/Chicago day, so the client must use the
// same anchor or a viewer east of CT could pick a date the server then rejects.
// It lives in weekDates.js — the single source of truth for date arithmetic in
// this package — rather than being re-derived here.
import { chicagoTodayIso, isIsoDate } from '../../../utils/weekDates.js';

/** `2026-01-05` → `Jan 5, 2026`. Formatted in UTC so it never shifts by zone. */
const START_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
// Total by construction, which the types alone do not guarantee here.
// `AdminUserDto` declares both date fields as required strings, but the client
// reads responses through `parseJson<T>` (see api/client.ts) — a type assertion
// with no runtime validation. So an API that omits them, which is exactly what a
// version-skewed deploy or the documented migration-0025 rollback produces,
// reaches this function with `undefined`, builds `new Date('undefinedT12:00:00Z')`,
// and throws `RangeError: Invalid time value` out of Intl during render, blanking
// the whole admin pane. Degrade to a dash instead.
function formatStartDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const parsed = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '—';
  return START_DATE_FMT.format(parsed);
}

// ── Icons ──────────────────────────────────────────────────────────────────
function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function XIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function StarIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function ShieldIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function RefreshIcon({ spinning }: { spinning?: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={spinning ? { animation: 'ur-spin 1s linear infinite' } : undefined}
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}
function CloudIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.5 19a4.5 4.5 0 1 0-1.4-8.78A6 6 0 1 0 6.06 17.5" />
    </svg>
  );
}
function ChevronDownIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function ChevronUpIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}
function InfoIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function PowerIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <line x1="12" y1="2" x2="12" y2="12" />
    </svg>
  );
}
function CheckCircleIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}
function SlashCircleIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}
function AlertTriangleIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Weekly-target edit field text: blank means "use the default". */
function targetInputText(weeklyMinuteTarget: number | null): string {
  return weeklyMinuteTarget === null ? '' : formatHours(weeklyMinuteTarget);
}

// ── AD Sync Panel ────────────────────────────────────────────────────────────
interface AdSyncPanelProps {
  lastSync: AdSyncResultDto | null;
  onSyncNow: () => void;
  syncing: boolean;
  config: AdSyncConfigDto | undefined;
  onSaveConfig: (data: UpdateAdSyncConfigRequest) => void;
  savingConfig: boolean;
}

function AdSyncPanel({
  lastSync,
  onSyncNow,
  syncing,
  config,
  onSaveConfig,
  savingConfig,
}: AdSyncPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ adGroup: '', syncHour: 2 });

  useEffect(() => {
    if (config && !editing) {
      setDraft({ adGroup: config.groupDn, syncHour: config.hourCt });
    }
  }, [config, editing]);

  const cnLabel = (draft.adGroup.match(/CN=([^,]+)/i)?.[1] ?? draft.adGroup) || '(not set)';

  function handleCancelEdit() {
    if (config) setDraft({ adGroup: config.groupDn, syncHour: config.hourCt });
    setEditing(false);
  }

  function handleSave() {
    onSaveConfig({
      enabled: config?.enabled ?? true,
      hourCt: draft.syncHour,
      groupDn: draft.adGroup,
    });
    setEditing(false);
  }

  function formatCtHour(h: number): string {
    const period = h < 12 ? 'AM' : 'PM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:00 ${period} CT`;
  }

  const syncTimeLabel = formatCtHour(draft.syncHour);

  return (
    <section
      className={'ur-ad-panel' + (expanded ? ' expanded' : ' collapsed')}
      aria-label="Active Directory sync"
    >
      <div className="ur-ad-head">
        <span className="ur-ad-ic">
          <CloudIcon />
        </span>
        <div className="ur-ad-title">
          <div className="t">Active Directory sync</div>
          <div className="s">
            {lastSync ? (
              lastSync.errors.length > 0 ? (
                <>
                  <span className="dot err" aria-hidden="true" /> Unhealthy · last ran{' '}
                  {new Date(lastSync.ranAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  · {lastSync.errors[0]}
                </>
              ) : (
                <>
                  <span className="dot ok" aria-hidden="true" /> Healthy · last ran{' '}
                  {new Date(lastSync.ranAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  · {lastSync.total} users
                </>
              )
            ) : (
              <>
                <span className="dot" aria-hidden="true" /> No sync recorded yet
              </>
            )}
          </div>
        </div>

        {!expanded && (
          <div className="ur-ad-quick" aria-hidden="true">
            <span className="ur-ad-quick-item">
              <span className="lbl">Group</span>
              <code>{cnLabel}</code>
            </span>
            <span className="ur-ad-quick-item">
              <span className="lbl">Daily at</span>
              <b>{syncTimeLabel}</b>
            </span>
          </div>
        )}

        <div className="ur-ad-actions">
          <button className="btn btn--primary btn--sm" onClick={onSyncNow} disabled={syncing}>
            <RefreshIcon spinning={syncing} />
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
          <button
            className={'btn btn--sm btn--light ur-ad-toggle' + (expanded ? ' active' : '')}
            onClick={() => {
              setExpanded((e) => !e);
              if (expanded) setEditing(false);
            }}
            aria-expanded={expanded}
            aria-controls="ur-ad-body"
          >
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            Sync settings
          </button>
        </div>
      </div>

      {expanded && (
        <div className="ur-ad-body" id="ur-ad-body">
          <div className="ur-ad-grid">
            <div className="ur-ad-field span-2">
              <label htmlFor="ur-ad-group">AD group</label>
              {editing ? (
                <input
                  id="ur-ad-group"
                  className="ur-input mono"
                  value={draft.adGroup}
                  onChange={(e) => setDraft((d) => ({ ...d, adGroup: e.target.value }))}
                  placeholder="CN=group-name,OU=Groups,DC=austin,DC=utexas,DC=edu"
                />
              ) : (
                <div className="ur-readonly mono">{draft.adGroup}</div>
              )}
              <div className="ur-field-hint">
                <InfoIcon /> Every member of this group becomes a Contributor automatically. Nested
                groups are expanded.
              </div>
            </div>
            <div className="ur-ad-field">
              <label htmlFor="ur-ad-time">Daily sync time</label>
              {editing ? (
                <div className="ur-time-row">
                  <input
                    id="ur-ad-time"
                    className="ur-input"
                    type="number"
                    min="0"
                    max="23"
                    step="1"
                    style={{ width: 72 }}
                    value={draft.syncHour}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        syncHour: Math.max(0, Math.min(23, Math.floor(Number(e.target.value)))),
                      }))
                    }
                  />
                  <span style={{ fontSize: 'var(--fs-13)', color: 'var(--fg3)' }}>
                    = {formatCtHour(draft.syncHour)}
                  </span>
                </div>
              ) : (
                <div className="ur-readonly">
                  <b>{syncTimeLabel}</b>
                </div>
              )}
              <div className="ur-field-hint">
                <InfoIcon /> Runs daily during this CT hour (exact minute varies)
              </div>
            </div>
            <div className="ur-ad-field">
              <label>Last sync</label>
              {lastSync ? (
                <>
                  <div
                    className={
                      'ur-last-stats' +
                      ((lastSync.pendingDeactivation ?? 0) > 0 ? ' has-pending' : '')
                    }
                  >
                    <div className="s-cell">
                      <div className="n">{lastSync.total}</div>
                      <div className="l">users</div>
                    </div>
                    <div className="s-cell pos">
                      <div className="n">+{lastSync.added}</div>
                      <div className="l">added</div>
                    </div>
                    <div className="s-cell neg">
                      <div className="n">−{lastSync.deactivated}</div>
                      <div className="l">removed</div>
                    </div>
                    {/* Undefined means "not reported" (a refused or failed run, or a
                        result stored before the grace period existed), not zero. */}
                    {(lastSync.pendingDeactivation ?? 0) > 0 && (
                      <div className="s-cell warn">
                        <div className="n">{lastSync.pendingDeactivation}</div>
                        <div className="l">pending removal</div>
                      </div>
                    )}
                  </div>
                  <div className="ur-field-hint muted">
                    Completed in {formatDuration(lastSync.durationMs)}
                  </div>
                </>
              ) : (
                <div className="ur-readonly muted" style={{ fontStyle: 'italic' }}>
                  N/A — no sync recorded yet
                </div>
              )}
            </div>
          </div>
          <div className="ur-ad-footer">
            {editing ? (
              <>
                <button className="btn btn--sm btn--light" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button
                  className="btn btn--sm btn--primary"
                  onClick={handleSave}
                  disabled={savingConfig}
                >
                  {savingConfig ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <button className="btn btn--sm btn--light" onClick={() => setEditing(true)}>
                <PencilIcon /> Edit
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ── User List ────────────────────────────────────────────────────────────────
type UserFilter = 'all' | 'admin' | 'approver';

interface UserListProps {
  users: AdminUserDto[];
  activeUserId: string | null;
  onSelect: (userId: string) => void;
  query: string;
  setQuery: (q: string) => void;
  filter: UserFilter;
  setFilter: (f: UserFilter) => void;
}

function UserList({
  users,
  activeUserId,
  onSelect,
  query,
  setQuery,
  filter,
  setFilter,
}: UserListProps) {
  const filtered = users.filter((u) => {
    if (filter === 'admin' && u.role !== 'administrator') return false;
    if (filter === 'approver' && u.role !== 'approver') return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      if (!u.displayName.toLowerCase().includes(q) && !(u.utEid ?? '').toLowerCase().includes(q))
        return false;
    }
    return true;
  });

  const adminCount = users.filter((u) => u.role === 'administrator').length;
  const approverCount = users.filter((u) => u.role === 'approver').length;

  const filters: { k: UserFilter; label: string; count: number }[] = [
    { k: 'all', label: 'All', count: users.length },
    { k: 'admin', label: 'Admins', count: adminCount },
    { k: 'approver', label: 'Approvers', count: approverCount },
  ];

  return (
    <aside className="ur-list" aria-label="Users list">
      <div className="ur-list-head">
        <div className="ur-search">
          <span className="icon">
            <SearchIcon />
          </span>
          <input
            placeholder="Search name or EID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search users"
          />
          {query && (
            <button className="ur-clear" onClick={() => setQuery('')} aria-label="Clear search">
              <XIcon size={11} />
            </button>
          )}
        </div>
        <div className="ur-list-filters" role="group" aria-label="Filter users by role">
          {filters.map((f) => (
            <button
              key={f.k}
              aria-pressed={filter === f.k}
              className={'ur-chip-tab' + (filter === f.k ? ' active' : '')}
              onClick={() => setFilter(f.k)}
            >
              {f.label} <span className="ct">{f.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="ur-list-body" role="listbox" aria-label="Users">
        {filtered.map((u) => (
          <button
            key={u.userId}
            role="option"
            aria-selected={u.userId === activeUserId}
            className={
              'ur-user-row' +
              (u.userId === activeUserId ? ' active' : '') +
              (!u.activeFlag ? ' inactive' : '')
            }
            onClick={() => onSelect(u.userId)}
          >
            <span className="ur-avatar" aria-hidden="true">
              {u.initials}
            </span>
            <div className="ur-user-info">
              <div className="nm">{u.displayName}</div>
              {u.utEid && <div className="eid">{u.utEid}</div>}
            </div>
            <div className="ur-user-badges">
              {u.role === 'administrator' && (
                <span className="ur-role-dot admin" title="Administrator">
                  <StarIcon size={9} />
                  <span className="sr-only">Administrator</span>
                </span>
              )}
              {u.role === 'approver' && (
                <span className="ur-role-dot appr" title="Approver">
                  <ShieldIcon size={9} />
                  <span className="sr-only">Approver</span>
                </span>
              )}
              {u.activeFlag && u.pendingDeactivationSince && (
                <span
                  className="ur-role-dot pending"
                  title="Pending removal: no longer in the synced AD group"
                >
                  <AlertTriangleIcon size={10} />
                  <span className="sr-only">Pending removal</span>
                </span>
              )}
            </div>
          </button>
        ))}

        {filtered.length === 0 && (
          <div className="ur-list-empty">
            {query ? (
              <>
                No one matches &ldquo;{query}&rdquo;.{' '}
                <button className="link" onClick={() => setQuery('')}>
                  Clear search
                </button>
              </>
            ) : (
              'No users match this filter.'
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

// ── Admin confirm dialog ─────────────────────────────────────────────────────
interface AdminConfirmDialogProps {
  user: AdminUserDto;
  action: 'grant' | 'revoke';
  onCancel: () => void;
  onConfirm: () => void;
  pending?: boolean;
}

function AdminConfirmDialog({
  user,
  action,
  onCancel,
  onConfirm,
  pending,
}: AdminConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, true);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const isGrant = action === 'grant';

  return (
    <div className="dialog-backdrop" onClick={onCancel}>
      <div
        ref={dialogRef}
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="admin-confirm-title"
        style={{ maxWidth: 460 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="head">
          <h3 id="admin-confirm-title">
            {isGrant ? 'Make this user an administrator?' : 'Remove administrator from this user?'}
          </h3>
          <div className="sub">
            <b style={{ color: 'var(--fg1)' }}>{user.displayName}</b>
            {user.utEid && <span style={{ color: 'var(--fg3)' }}> · {user.utEid}</span>}
          </div>
        </div>
        <div className="body">
          <p style={{ margin: 0, fontSize: 'var(--fs-13)', color: 'var(--fg2)', lineHeight: 1.55 }}>
            {isGrant
              ? 'Administrators can manage all teams, projects, categories, and other users — including granting or removing administrator from anyone. Use sparingly.'
              : "They'll lose access to system administration immediately. Approver status from team memberships is unaffected."}
          </p>
        </div>
        <div className="footer">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          {isGrant ? (
            <button className="btn btn--primary" onClick={onConfirm} disabled={pending} autoFocus>
              <StarIcon size={12} /> {pending ? 'Saving…' : 'Make admin'}
            </button>
          ) : (
            <button
              className="btn btn--destructive"
              onClick={onConfirm}
              disabled={pending}
              autoFocus
            >
              <XIcon size={12} /> {pending ? 'Saving…' : 'Remove admin'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Deactivate / reactivate confirm dialog ───────────────────────────────────
interface ActiveConfirmDialogProps {
  user: AdminUserDto;
  action: 'deactivate' | 'reactivate';
  onCancel: () => void;
  onConfirm: () => void;
  pending?: boolean;
  error?: string;
}

function ActiveConfirmDialog({
  user,
  action,
  onCancel,
  onConfirm,
  pending,
  error,
}: ActiveConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, true);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Don't dismiss while the mutation is in flight (matches the disabled
      // Cancel button) so the in-progress state stays visible.
      if (e.key === 'Escape' && !pending) onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, pending]);

  const isDeactivate = action === 'deactivate';

  return (
    <div className="dialog-backdrop" onClick={pending ? undefined : onCancel}>
      <div
        ref={dialogRef}
        className="dialog"
        // Deactivation revokes access immediately (urgent/consequential) →
        // alertdialog. Reactivation is reversible and low-risk → plain dialog.
        role={isDeactivate ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby="active-confirm-title"
        aria-describedby="active-confirm-desc"
        style={{ maxWidth: 460 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="head">
          <h3 id="active-confirm-title">
            {isDeactivate ? 'Deactivate this user?' : 'Reactivate this user?'}
          </h3>
          <div className="sub">
            <b style={{ color: 'var(--fg1)' }}>{user.displayName}</b>
            {user.utEid && <span style={{ color: 'var(--fg3)' }}> · {user.utEid}</span>}
          </div>
        </div>
        <div className="body">
          <p
            id="active-confirm-desc"
            style={{ margin: 0, fontSize: 'var(--fs-13)', color: 'var(--fg2)', lineHeight: 1.55 }}
          >
            {isDeactivate
              ? "They'll lose access to the app immediately. This deactivation is preserved across Active Directory syncs until you reactivate them. Their timesheet history is kept."
              : 'They regain access to the app immediately, and the admin deactivation is cleared so Active Directory sync resumes managing their account normally.'}
          </p>
          {error && (
            <p role="alert" className="ur-active-dialog-error">
              <AlertTriangleIcon /> {error}
            </p>
          )}
        </div>
        <div className="footer">
          <button className="btn" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          {isDeactivate ? (
            <button className="btn btn--caution" onClick={onConfirm} disabled={pending} autoFocus>
              <PowerIcon size={12} /> {pending ? 'Deactivating…' : 'Deactivate user'}
            </button>
          ) : (
            <button className="btn btn--primary" onClick={onConfirm} disabled={pending} autoFocus>
              <PowerIcon size={12} /> {pending ? 'Reactivating…' : 'Reactivate user'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── User Profile ─────────────────────────────────────────────────────────────
interface UserProfileProps {
  user: AdminUserDto;
  onAdminChangeRequested: (userId: string) => void;
  onActiveChangeRequested: (userId: string) => void;
  onUpdateHourTarget: (
    userId: string,
    weeklyMinuteTarget: number | null,
    opts: { onSuccess: () => void; onError: (err: Error) => void },
  ) => void;
  isUpdatingHourTarget: boolean;
  onUpdateSystemStartDate: (
    userId: string,
    systemStartDate: string | null,
    opts: { onSuccess: () => void; onError: (err: Error) => void },
  ) => void;
  isUpdatingSystemStartDate: boolean;
  onNavigate: (tab: string) => void;
}

function UserProfile({
  user,
  onAdminChangeRequested,
  onActiveChangeRequested,
  onUpdateHourTarget,
  isUpdatingHourTarget,
  onUpdateSystemStartDate,
  isUpdatingSystemStartDate,
  onNavigate,
}: UserProfileProps) {
  const membershipsQuery = useAdminUserMemberships(user.userId);
  const linesQuery = useAdminUserLines(user.userId);

  const resolvedHours = targetInputText(user.weeklyMinuteTarget);
  const [hourTargetValue, setHourTargetValue] = useState(resolvedHours);
  const [editingHourTarget, setEditingHourTarget] = useState(false);
  const [hourTargetError, setHourTargetError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const editButtonRef = useRef<HTMLButtonElement>(null);

  const [startDateValue, setStartDateValue] = useState(user.systemStartDate ?? '');
  const [editingStartDate, setEditingStartDate] = useState(false);
  const [startDateError, setStartDateError] = useState('');
  const startDateEditButtonRef = useRef<HTMLButtonElement>(null);

  // Reset local state when a different user is selected
  useEffect(() => {
    setHourTargetValue(targetInputText(user.weeklyMinuteTarget));
    setEditingHourTarget(false);
    setHourTargetError('');
    setSaveStatus('');
  }, [user.userId, user.weeklyMinuteTarget]);

  useEffect(() => {
    setStartDateValue(user.systemStartDate ?? '');
    setEditingStartDate(false);
    setStartDateError('');
  }, [user.userId, user.systemStartDate]);

  function handleHourTargetSave() {
    const trimmed = hourTargetValue.trim();
    let minuteTarget: number | null;
    if (trimmed === '') {
      minuteTarget = null;
    } else {
      const hours = parseFloat(trimmed);
      if (isNaN(hours) || hours < 1 || hours > 168) {
        setHourTargetError('Enter a number between 1 and 168, or leave blank for the default.');
        return;
      }
      minuteTarget = Math.round(hours * 60);
    }
    setHourTargetError('');
    onUpdateHourTarget(user.userId, minuteTarget, {
      onSuccess: () => {
        setSaveStatus(
          minuteTarget === null
            ? 'Weekly hour target reset to default.'
            : 'Weekly hour target saved.',
        );
        setEditingHourTarget(false);
        requestAnimationFrame(() => editButtonRef.current?.focus());
      },
      onError: (err: Error) => {
        setHourTargetError(err.message || 'Failed to save. Please try again.');
      },
    });
  }

  function handleHourTargetCancel() {
    setHourTargetValue(targetInputText(user.weeklyMinuteTarget));
    setHourTargetError('');
    setEditingHourTarget(false);
    requestAnimationFrame(() => editButtonRef.current?.focus());
  }

  function handleStartDateSave() {
    const trimmed = startDateValue.trim();
    let startDate: string | null;
    if (trimmed === '') {
      startDate = null;
    } else if (!isIsoDate(trimmed)) {
      setStartDateError('Enter a valid date, or leave blank to use the account creation week.');
      return;
    } else if (trimmed > chicagoTodayIso()) {
      setStartDateError('A system start date cannot be in the future.');
      return;
    } else {
      startDate = trimmed;
    }
    setStartDateError('');
    onUpdateSystemStartDate(user.userId, startDate, {
      onSuccess: () => {
        setSaveStatus(
          startDate === null
            ? 'System start date reset to the account creation week.'
            : 'System start date saved.',
        );
        setEditingStartDate(false);
        requestAnimationFrame(() => startDateEditButtonRef.current?.focus());
      },
      onError: (err: Error) => {
        setStartDateError(err.message || 'Failed to save. Please try again.');
      },
    });
  }

  function handleStartDateCancel() {
    setStartDateValue(user.systemStartDate ?? '');
    setStartDateError('');
    setEditingStartDate(false);
    requestAnimationFrame(() => startDateEditButtonRef.current?.focus());
  }

  const memberships = membershipsQuery.data ?? [];
  const lines = linesQuery.data ?? [];

  const totalHours = lines.reduce((sum, l) => sum + l.hoursYtd, 0);
  const teamCount = lines.filter((l) => l.source === 'team_fan_out').length;
  const directCount = lines.filter((l) => l.source === 'direct').length;

  const sortedMemberships = [...memberships].sort((a, b) => {
    const order = (m: AdminUserMembershipDto) => (m.isLead ? 0 : m.role === 'approver' ? 1 : 2);
    return order(a) - order(b) || a.teamName.localeCompare(b.teamName);
  });

  return (
    <div className="ur-profile">
      <header className="ur-profile-head">
        <span className="ur-avatar-lg" aria-hidden="true">
          {user.initials}
        </span>
        <div className="ur-profile-id">
          <h2>{user.displayName}</h2>
          <div className="meta">
            {user.utEid && <span className="eid">{user.utEid}</span>}
            {user.role === 'administrator' && (
              <span className="ur-role-pill admin">
                <StarIcon size={11} /> Administrator
              </span>
            )}
            {user.role === 'approver' && (
              <span className="ur-role-pill appr">
                <ShieldIcon size={11} /> Approver
              </span>
            )}
            <span className={'ur-status-pill ' + (user.activeFlag ? 'active' : 'inactive')}>
              {user.activeFlag ? (
                <>
                  <CheckCircleIcon size={11} /> Active
                </>
              ) : (
                <>
                  <SlashCircleIcon size={11} /> Inactive
                </>
              )}
            </span>
            {user.activeFlag && user.pendingDeactivationSince && (
              <span className="ur-status-pill pending">
                <AlertTriangleIcon size={11} /> Pending removal: not in the AD group since{' '}
                {new Date(user.pendingDeactivationSince).toLocaleDateString([], {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            )}
          </div>
        </div>

        <div className="ur-profile-actions">
          {user.role === 'administrator' ? (
            <button
              className="btn btn--sm danger-ghost"
              onClick={() => onAdminChangeRequested(user.userId)}
            >
              <XIcon size={12} /> Remove admin
            </button>
          ) : (
            <button
              className="btn btn--sm btn--light"
              onClick={() => onAdminChangeRequested(user.userId)}
            >
              <StarIcon size={12} /> Make admin
            </button>
          )}
          {user.activeFlag ? (
            <button
              className="btn btn--sm btn--caution-ghost"
              onClick={() => onActiveChangeRequested(user.userId)}
              aria-label={`Deactivate ${user.displayName}`}
            >
              <PowerIcon size={12} /> Deactivate
            </button>
          ) : (
            <button
              className="btn btn--sm btn--light"
              onClick={() => onActiveChangeRequested(user.userId)}
              aria-label={`Reactivate ${user.displayName}`}
            >
              <PowerIcon size={12} /> Reactivate
            </button>
          )}
        </div>
      </header>

      <div className="ur-profile-body">
        {/* Teams card */}
        <section className="ur-card ur-teams-card" aria-label="Team memberships">
          <div className="ur-card-head">
            <h3>
              <UsersIcon /> Teams
            </h3>
            <span className="ct">{sortedMemberships.length}</span>
          </div>

          {membershipsQuery.isLoading ? (
            <div className="ur-card-loading">Loading…</div>
          ) : (
            <div className="ur-team-pills">
              {sortedMemberships.length === 0 && (
                <div className="ur-empty-note">Not currently on any team.</div>
              )}
              {sortedMemberships.map((m) => (
                <div
                  key={m.teamId}
                  className={'ur-team-pill role-' + (m.isLead ? 'lead' : m.role)}
                  style={{ borderColor: m.color }}
                >
                  <span className="swatch" style={{ background: m.color }} aria-hidden="true" />
                  <span className="nm">{m.teamName}</span>
                  {m.isLead && (
                    <span className="ur-team-role lead">
                      <StarIcon size={10} /> Lead
                    </span>
                  )}
                  {!m.isLead && m.role === 'approver' && (
                    <span className="ur-team-role appr">
                      <ShieldIcon size={10} /> Approver
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="ur-card-foot muted">
            <InfoIcon />
            <span>
              Edit membership on the{' '}
              <button type="button" className="ur-inline-link" onClick={() => onNavigate('teams')}>
                Team management
              </button>{' '}
              tab.
            </span>
          </div>
        </section>

        {/* Hour target card */}
        <section className="ur-card" aria-labelledby="ts-settings-heading">
          <div className="ur-card-head">
            <h3 id="ts-settings-heading">Timesheet settings</h3>
          </div>
          <div style={{ padding: '12px 16px 16px' }}>
            <div role="status" aria-live="polite" className="sr-only">
              {saveStatus}
            </div>
            {editingHourTarget ? (
              <div>
                <label
                  htmlFor="weekly-target"
                  style={{
                    display: 'block',
                    fontSize: 'var(--fs-13)',
                    color: 'var(--fg2)',
                    marginBottom: 6,
                  }}
                >
                  Weekly hour target
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    id="weekly-target"
                    type="number"
                    min="1"
                    max="168"
                    step="0.5"
                    className="ur-input"
                    style={{ width: 90 }}
                    value={hourTargetValue}
                    onChange={(e) => {
                      setHourTargetValue(e.target.value);
                      setHourTargetError('');
                    }}
                    placeholder="40"
                    aria-describedby={
                      hourTargetError
                        ? 'weekly-target-error weekly-target-hint'
                        : 'weekly-target-hint'
                    }
                    aria-invalid={hourTargetError ? 'true' : undefined}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleHourTargetSave();
                      if (e.key === 'Escape') handleHourTargetCancel();
                    }}
                  />
                  <button
                    className="btn btn--sm btn--primary"
                    onClick={handleHourTargetSave}
                    disabled={isUpdatingHourTarget}
                  >
                    {isUpdatingHourTarget ? 'Saving…' : 'Save'}
                  </button>
                  <button className="btn btn--sm btn--light" onClick={handleHourTargetCancel}>
                    Cancel
                  </button>
                </div>
                {hourTargetError && (
                  <div
                    id="weekly-target-error"
                    role="alert"
                    style={{ fontSize: 'var(--fs-12)', color: 'var(--fg1)', marginTop: 4 }}
                  >
                    {hourTargetError}
                  </div>
                )}
              </div>
            ) : (
              <div role="group" aria-labelledby="weekly-target-label">
                <p
                  id="weekly-target-label"
                  style={{
                    fontSize: 'var(--fs-13)',
                    color: 'var(--fg2)',
                    marginBottom: 6,
                    margin: '0 0 6px',
                  }}
                >
                  Weekly hour target
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--fs-14)', color: 'var(--fg1)', fontWeight: 500 }}>
                    {user.weeklyMinuteTarget !== null
                      ? `${formatHours(user.weeklyMinuteTarget)}h`
                      : '40h (default)'}
                  </span>
                  <button
                    ref={editButtonRef}
                    className="btn btn--sm btn--light"
                    onClick={() => {
                      setEditingHourTarget(true);
                      setSaveStatus('');
                    }}
                    aria-label="Edit weekly hour target"
                  >
                    <PencilIcon /> Edit
                  </button>
                </div>
              </div>
            )}
            <div
              id="weekly-target-hint"
              style={{ fontSize: 'var(--fs-12)', color: 'var(--fg3)', marginTop: 6 }}
            >
              Minimum hours a contributor must log before submitting. Leave blank to use the default
              (40h).
            </div>

            <hr
              style={{
                border: 0,
                borderTop: '1px solid var(--border)',
                margin: '16px 0',
              }}
            />

            {editingStartDate ? (
              <div>
                <label
                  htmlFor="system-start-date"
                  style={{
                    display: 'block',
                    fontSize: 'var(--fs-13)',
                    color: 'var(--fg2)',
                    marginBottom: 6,
                  }}
                >
                  System start date
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    id="system-start-date"
                    type="date"
                    max={chicagoTodayIso()}
                    className="ur-input"
                    value={startDateValue}
                    onChange={(e) => {
                      setStartDateValue(e.target.value);
                      setStartDateError('');
                    }}
                    aria-describedby={
                      startDateError
                        ? 'system-start-date-error system-start-date-hint'
                        : 'system-start-date-hint'
                    }
                    aria-invalid={startDateError ? 'true' : undefined}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleStartDateSave();
                      if (e.key === 'Escape') handleStartDateCancel();
                    }}
                  />
                  <button
                    className="btn btn--sm btn--primary"
                    onClick={handleStartDateSave}
                    disabled={isUpdatingSystemStartDate}
                  >
                    {isUpdatingSystemStartDate ? 'Saving…' : 'Save'}
                  </button>
                  <button className="btn btn--sm btn--light" onClick={handleStartDateCancel}>
                    Cancel
                  </button>
                </div>
                {startDateError && (
                  <div
                    id="system-start-date-error"
                    role="alert"
                    style={{ fontSize: 'var(--fs-12)', color: 'var(--fg1)', marginTop: 4 }}
                  >
                    {startDateError}
                  </div>
                )}
              </div>
            ) : (
              <div role="group" aria-labelledby="system-start-date-label">
                <p
                  id="system-start-date-label"
                  style={{
                    fontSize: 'var(--fs-13)',
                    color: 'var(--fg2)',
                    margin: '0 0 6px',
                  }}
                >
                  System start date
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--fs-14)', color: 'var(--fg1)', fontWeight: 500 }}>
                    {/* Truthiness, not `!== null`: an absent field is `undefined`,
                        which `!== null` would treat as a stored override. */}
                    {user.systemStartDate
                      ? formatStartDate(user.systemStartDate)
                      : user.effectiveSystemStartDate
                        ? `${formatStartDate(user.effectiveSystemStartDate)} (from account creation)`
                        : '—'}
                  </span>
                  <button
                    ref={startDateEditButtonRef}
                    className="btn btn--sm btn--light"
                    onClick={() => {
                      setEditingStartDate(true);
                      setSaveStatus('');
                    }}
                    aria-label="Edit system start date"
                  >
                    <PencilIcon /> Edit
                  </button>
                </div>
              </div>
            )}
            <div
              id="system-start-date-hint"
              style={{ fontSize: 'var(--fs-12)', color: 'var(--fg3)', marginTop: 6 }}
            >
              The date this person landed in the system. Reminders and the Needs action list skip
              blank and draft weeks before it. Submitted and returned sheets always show. Leave
              blank to use the week the account was created.
            </div>
          </div>
        </section>

        {/* Timesheet lines card */}
        <section className="ur-card ur-lines-card" aria-label="Timesheet lines">
          <div className="ur-card-head">
            <h3>Timesheet lines</h3>
            <span className="ct">{lines.length}</span>
            {lines.length > 0 && (
              <span className="ur-card-stats">
                <span>
                  <b>{formatHourValue(totalHours)}h</b> FY-to-date
                </span>
                <span className="muted">·</span>
                <span className="muted">
                  {teamCount} via team · {directCount} individual
                </span>
              </span>
            )}
          </div>

          {linesQuery.isLoading ? (
            <div className="ur-card-loading">Loading…</div>
          ) : lines.length === 0 ? (
            <div className="ur-empty-note" style={{ padding: '24px', textAlign: 'center' }}>
              No timesheet lines yet — this user isn&apos;t on any tasks.
            </div>
          ) : (
            <div className="ur-lines-table" role="table" aria-label="Timesheet lines">
              <div className="ur-lines-row head" role="row">
                <div className="cell" role="columnheader">
                  Project
                </div>
                <div className="cell" role="columnheader">
                  Task
                </div>
                <div className="cell" role="columnheader">
                  Assigned via
                </div>
                <div className="cell right" role="columnheader">
                  Hours (FY)
                </div>
              </div>
              {lines.map((l) => (
                <div
                  key={l.assignmentId}
                  className={'ur-lines-row' + (!l.activeFlag ? ' inactive' : '')}
                  role="row"
                >
                  <div className="cell project" role="cell">
                    <div className="nm">{l.projectName}</div>
                    <div className="code">{l.projectCode}</div>
                  </div>
                  <div className="cell task" role="cell" title={l.taskName}>
                    {l.taskName}
                  </div>
                  <div className="cell source" role="cell">
                    {!l.activeFlag ? (
                      <span className="src-chip removed">Removed</span>
                    ) : l.source === 'team_fan_out' && l.sourceTeam ? (
                      <span className="src-chip team">
                        <UsersIcon /> {l.sourceTeam.teamName}
                      </span>
                    ) : l.source === 'team_fan_out' ? (
                      <span className="src-chip team">
                        <UsersIcon /> Team
                      </span>
                    ) : (
                      <span className="src-chip indiv">Individual</span>
                    )}
                  </div>
                  <div className="cell right hours" role="cell">
                    {l.hoursYtd === 0 ? '—' : `${formatHourValue(l.hoursYtd)}h`}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="ur-card-foot muted">
            <InfoIcon />
            <span>
              Manage assignments on the{' '}
              <button
                type="button"
                className="ur-inline-link"
                onClick={() => onNavigate('projects')}
              >
                Projects &amp; tasks
              </button>{' '}
              page.
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function ProfileEmpty() {
  return (
    <div className="ur-profile empty">
      <UsersIcon />
      <h3>Pick someone to see their profile</h3>
      <p>Search or scroll the list to find a user.</p>
    </div>
  );
}

// ── Top-level pane ────────────────────────────────────────────────────────────
interface UsersPaneProps {
  onNavigate?: (tab: string) => void;
}

// "Sync now" returns as soon as the run is accepted; the outcome arrives by
// polling. Three seconds keeps the wait responsive without hammering the API for
// a job that now takes a few seconds.
const AD_SYNC_POLL_MS = 3_000;
// Stop waiting only once the run genuinely cannot still be in progress.
//
// This was 6 minutes, on the reasoning that it sat just past the sync Lambda's
// own 5-minute timeout. That premise was wrong: the function has no
// EventInvokeConfig, so an async invoke carries Lambda's default policy of two
// further attempts. A run killed by its timeout persists no result and is then
// retried, so the real worst case is three 5-minute attempts plus backoff — and
// giving up at 6 minutes told the operator to stop waiting while the roster
// could still change underneath them.
//
// Sized to outlast that lifecycle instead. It only bites in the pathological
// case where a sync hangs; a healthy run finishes in seconds.
const AD_SYNC_POLL_TIMEOUT_MS = 20 * 60 * 1_000;

export function UsersPane({ onNavigate }: UsersPaneProps) {
  const usersQuery = useAdminUsers();
  const updateRole = useUpdateUserRole();
  const updateHourTarget = useUpdateUserHourTarget();
  const updateSystemStartDate = useUpdateUserSystemStartDate();
  const setUserActive = useSetUserActive();
  const adSync = useAdSyncNow();
  // The id of the run this pane started, from the 202. Non-null means a run is in
  // flight, which both drives the spinner and turns on polling.
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);
  const adSyncConfig = useAdSyncConfig(pendingRunId ? AD_SYNC_POLL_MS : false);
  const updateAdSyncConfig = useUpdateAdSyncConfig();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<UserFilter>('all');
  const [activeUserId, setActiveUserId] = useState<string | null>(null);
  const [confirmAdmin, setConfirmAdmin] = useState<{
    userId: string;
    action: 'grant' | 'revoke';
  } | null>(null);
  const [confirmActive, setConfirmActive] = useState<{
    userId: string;
    action: 'deactivate' | 'reactivate';
  } | null>(null);
  const [activeError, setActiveError] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [syncStatus, setSyncStatus] = useState('');
  const queryClient = useQueryClient();

  const users = usersQuery.data ?? [];
  const activeUser = users.find((u) => u.userId === activeUserId) ?? null;

  // Announce through the live region. Clearing first and setting on the next
  // frame is what makes an identical message announce twice — two runs with the
  // same counts, or two consecutive timeouts, otherwise leave the DOM value
  // unchanged and screen readers stay silent (WCAG 4.1.3 Status Messages).
  const announceSync = useCallback((message: string) => {
    setSyncStatus('');
    requestAnimationFrame(() => setSyncStatus(message));
  }, []);

  const handleSyncNow = useCallback(() => {
    // The run outlives the request, so the busy state is driven by the poll below
    // rather than by the mutation. The id comes from the response rather than
    // from the cached config, which may be stale — a scheduled run that landed
    // since the last fetch would otherwise look like this run completing
    // instantly.
    adSync.mutate(undefined, {
      onSuccess: (started) => setPendingRunId(started.runId),
      onError: () => setPendingRunId(null),
    });
  }, [adSync]);

  // Finished when a result carrying *this* run's id appears. Runs overlap by
  // design — a scheduled run and this one serialize on the roster lock — so
  // "lastSync changed" would report completion off somebody else's run and could
  // show their outcome as this one's.
  const lastSync = adSyncConfig.data?.lastSync ?? null;
  const recentRunIds = adSyncConfig.data?.recentRunIds;
  useEffect(() => {
    if (!pendingRunId) return;
    // Check the recent-run list as well as the latest-result slot. That slot
    // holds one value, and a run queued behind this one can overwrite it inside
    // a single poll interval — this run would then never be observed there, and
    // the wait would run to its timeout and report a failure for a sync that
    // succeeded.
    const finished = lastSync?.runId === pendingRunId || recentRunIds?.includes(pendingRunId);
    if (!finished) return;
    setPendingRunId(null);
    // A whole-roster sync can change display names, EIDs, email and active state,
    // which are denormalised into other cached views — the signed-in user's own
    // profile, approver inboxes. This runs rarely and by hand, so dropping the
    // whole cache is cheaper than enumerating every user-backed key and missing
    // one.
    void queryClient.invalidateQueries();
    // Only describe the outcome when the displayed result is *this* run's.
    //
    // Completion can be detected through the recent-run list while `lastSync`
    // already belongs to a later run, and announcing that run's counts would
    // describe somebody else's outcome as the result of the button the user just
    // pressed — a failed run followed by a successful scheduled one would be
    // announced as a success. The panel showing the newest result is correct;
    // narrating it as this action's outcome is not.
    const isOwnResult = lastSync?.runId === pendingRunId;
    announceSync(
      !isOwnResult
        ? 'Sync finished. A more recent sync result is now shown.'
        : lastSync.errors.length === 0
          ? `Sync finished. ${String(lastSync.added)} added, ${String(lastSync.updated)} updated, ${String(lastSync.deactivated)} deactivated.` +
            ((lastSync.pendingDeactivation ?? 0) > 0
              ? ` ${String(lastSync.pendingDeactivation)} pending removal.`
              : '')
          : 'Sync finished with errors. See the last sync result.',
    );
  }, [pendingRunId, lastSync, recentRunIds, queryClient, announceSync]);

  // Stop waiting if the run never reports back — a Lambda killed by its own
  // timeout writes no result, and a spinner that never resolves is worse than an
  // error the admin can act on.
  useEffect(() => {
    if (!pendingRunId) return;
    const timer = setTimeout(() => {
      setPendingRunId(null);
      announceSync(
        'Sync has not reported back. It may still be retrying — check the last sync result shortly.',
      );
    }, AD_SYNC_POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pendingRunId, announceSync]);

  const handleAdminChangeRequested = useCallback(
    (userId: string) => {
      const user = users.find((u) => u.userId === userId);
      if (!user) return;
      setConfirmAdmin({ userId, action: user.role === 'administrator' ? 'revoke' : 'grant' });
    },
    [users],
  );

  const handleAdminConfirm = useCallback(() => {
    if (!confirmAdmin) return;
    const newRole = confirmAdmin.action === 'grant' ? 'administrator' : 'contributor';
    updateRole.mutate(
      { userId: confirmAdmin.userId, role: newRole },
      {
        onSuccess: () => setConfirmAdmin(null),
      },
    );
  }, [confirmAdmin, updateRole]);

  const handleActiveChangeRequested = useCallback(
    (userId: string) => {
      const user = users.find((u) => u.userId === userId);
      if (!user) return;
      setActiveError('');
      setConfirmActive({ userId, action: user.activeFlag ? 'deactivate' : 'reactivate' });
    },
    [users],
  );

  const handleActiveConfirm = useCallback(() => {
    if (!confirmActive) return;
    const active = confirmActive.action === 'reactivate';
    const user = users.find((u) => u.userId === confirmActive.userId);
    setActiveError(''); // clear any stale error so a retry shows a clean pending state
    setUserActive.mutate(
      { userId: confirmActive.userId, active },
      {
        onSuccess: () => {
          setActiveError('');
          setConfirmActive(null);
          // Announce the result to assistive tech — the status pill flips
          // silently otherwise (WCAG 4.1.3 Status Messages). Clear first, then set
          // on the next frame so the live region re-announces even when the message
          // text is identical to the previous one (same user toggled twice).
          if (user) {
            const message = active
              ? `${user.displayName} has been reactivated.`
              : `${user.displayName} has been deactivated.`;
            setActiveStatus('');
            requestAnimationFrame(() => setActiveStatus(message));
          }
        },
        onError: (err: Error) => {
          setActiveError(err.message || 'Something went wrong. Please try again.');
        },
      },
    );
  }, [confirmActive, setUserActive, users]);

  if (usersQuery.isLoading) {
    return <div className="ur-shell ur-loading">Loading users…</div>;
  }

  return (
    <div className="ur-shell">
      <div role="status" aria-live="polite" className="sr-only">
        {activeStatus}
      </div>
      <div role="status" aria-live="polite" className="sr-only">
        {syncStatus}
      </div>
      <AdSyncPanel
        lastSync={adSyncConfig.data?.lastSync ?? null}
        onSyncNow={handleSyncNow}
        syncing={adSync.isPending || pendingRunId !== null}
        config={adSyncConfig.data}
        onSaveConfig={updateAdSyncConfig.mutate}
        savingConfig={updateAdSyncConfig.isPending}
      />

      <div className="ur-main">
        <UserList
          users={users}
          activeUserId={activeUserId}
          onSelect={setActiveUserId}
          query={query}
          setQuery={setQuery}
          filter={filter}
          setFilter={setFilter}
        />

        {activeUser ? (
          <UserProfile
            user={activeUser}
            onAdminChangeRequested={handleAdminChangeRequested}
            onActiveChangeRequested={handleActiveChangeRequested}
            onUpdateHourTarget={(userId, weeklyMinuteTarget, opts) =>
              updateHourTarget.mutate({ userId, weeklyMinuteTarget }, opts)
            }
            isUpdatingHourTarget={updateHourTarget.isPending}
            onUpdateSystemStartDate={(userId, systemStartDate, opts) =>
              updateSystemStartDate.mutate({ userId, systemStartDate }, opts)
            }
            isUpdatingSystemStartDate={updateSystemStartDate.isPending}
            onNavigate={onNavigate ?? (() => undefined)}
          />
        ) : (
          <ProfileEmpty />
        )}
      </div>

      {confirmAdmin &&
        (() => {
          const user = users.find((u) => u.userId === confirmAdmin.userId);
          return user ? (
            <AdminConfirmDialog
              user={user}
              action={confirmAdmin.action}
              onCancel={() => setConfirmAdmin(null)}
              onConfirm={handleAdminConfirm}
              pending={updateRole.isPending}
            />
          ) : null;
        })()}

      {confirmActive &&
        (() => {
          const user = users.find((u) => u.userId === confirmActive.userId);
          return user ? (
            <ActiveConfirmDialog
              user={user}
              action={confirmActive.action}
              onCancel={() => {
                setActiveError('');
                setConfirmActive(null);
              }}
              onConfirm={handleActiveConfirm}
              pending={setUserActive.isPending}
              error={activeError}
            />
          ) : null;
        })()}
    </div>
  );
}
